// src/services/api.ts
import axios from 'axios';
import { clearAuthSession, getToken } from '../utils/auth';

// ===== Base =====
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api';
const ONE_HOUR_MS = 60 * 60 * 1000;

export const API = axios.create({
  baseURL: BASE_URL,
  timeout: ONE_HOUR_MS,
});

// Attach JWT
API.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    // ensure headers object exists
    if (!config.headers) {
      config.headers = {} as any;
    }
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401s (expired/missing token → redirect to login)
API.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      console.warn('401 from API:', err?.response?.data || err?.message);
      clearAuthSession();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ===== Helpers =====
const postOrPut = async (method: 'post' | 'put', url: string, data: any, config?: Record<string, any>) => {
  const isFD = typeof FormData !== 'undefined' && data instanceof FormData;
  const headers = isFD ? { 'Content-Type': 'multipart/form-data' } : undefined;
  const resp = await API.request({ url, method, data, headers, ...config });
  return resp.data;
};

// unwrap lists whether backend returns: {items:[...]}, {projects:[...]}, or [...]
const unwrapList = <T = any>(data: any, keys: string[]): T[] => {
  for (const k of keys) {
    if (data && Array.isArray(data[k])) return data[k] as T[];
  }
  return Array.isArray(data) ? (data as T[]) : [];
};

// ===================== NOTES =====================
export const fetchNotes = (search = '', date = '') =>
  API.get('/notes', { params: { search, date } }); // caller uses res.data directly (shape may vary)

export const getNoteById = (id: string) =>
  API.get(`/notes/${id}`).then((r) => r.data);

export const createNote = (data: any) =>
  postOrPut('post', '/notes', data);

export const updateNote = (id: string, data: any) =>
  postOrPut('put', `/notes/${id}`, data);

export const deleteNote = (id: string) =>
  API.delete(`/notes/${id}`).then((r) => r.data);

// ===================== PROJECTS =====================
export const listProjects = () =>
  API.get('/projects').then((r) => unwrapList<{ _id: string; name: string }>(r.data, ['projects', 'items']));

export const createProject = (name: string) =>
  API.post('/projects', { name }).then((r) => r.data);

export const updateProject = (id: string, name: string) =>
  API.put(`/projects/${id}`, { name }).then((r) => r.data);

export const deleteProject = (id: string) =>
  API.delete(`/projects/${id}`).then((r) => r.data);

// ===================== ASSIGNEES =====================
export const listAssignees = () =>
  API.get('/assignees').then((r) => unwrapList<{ _id: string; name: string; email?: string; username?: string }>(r.data, ['assignees', 'items']));

export const createAssignee = (payload: { name: string; email?: string; username: string; password: string }) =>
  API.post('/assignees', payload).then((r) => r.data);

export const updateAssignee = (id: string, payload: { name?: string; email?: string; username?: string; password?: string }) =>
  API.put(`/assignees/${id}`, payload).then((r) => r.data);

export const deleteAssignee = (id: string) =>
  API.delete(`/assignees/${id}`).then((r) => r.data);

// ===================== STATUSES =====================
export const listStatuses = () =>
  API.get('/statuses').then((r) => unwrapList<{ _id: string; name: string }>(r.data, ['statuses', 'items']));

export const createStatus = (name: string) =>
  API.post('/statuses', { name }).then((r) => r.data);

export const updateStatus = (id: string, name: string) =>
  API.put(`/statuses/${id}`, { name }).then((r) => r.data);

export const deleteStatus = (id: string) =>
  API.delete(`/statuses/${id}`).then((r) => r.data);

// ===================== PAYMENT TYPES =====================
export const listPaymentTypes = () =>
  API.get('/payment-types').then((r) => unwrapList<{ _id: string; name: string }>(r.data, ['paymentTypes', 'items']));

export const createPaymentType = (name: string) =>
  API.post('/payment-types', { name }).then((r) => r.data);

export const updatePaymentType = (id: string, name: string) =>
  API.put(`/payment-types/${id}`, { name }).then((r) => r.data);

export const deletePaymentType = (id: string) =>
  API.delete(`/payment-types/${id}`).then((r) => r.data);

// ===================== EXPENSE TYPES =====================
export const listExpenseTypes = () =>
  API.get('/expense-froms').then((r) => unwrapList<{ _id: string; name: string }>(r.data, ['expenseFroms', 'items']));

export const createExpenseType = (name: string) =>
  API.post('/expense-froms', { name }).then((r) => r.data);

export const updateExpenseType = (id: string, name: string) =>
  API.put(`/expense-froms/${id}`, { name }).then((r) => r.data);

export const deleteExpenseType = (id: string) =>
  API.delete(`/expense-froms/${id}`).then((r) => r.data);

export const listExpenseFroms = listExpenseTypes;
export const createExpenseFrom = createExpenseType;
export const updateExpenseFrom = updateExpenseType;
export const deleteExpenseFrom = deleteExpenseType;

// ===================== EXPENSE CATEGORIES =====================
export const listExpenseCategories = () =>
  API.get('/expense-categories').then((r) => unwrapList<{ _id: string; name: string }>(r.data, ['expenseCategories', 'items']));

export const createExpenseCategory = (name: string) =>
  API.post('/expense-categories', { name }).then((r) => r.data);

export const updateExpenseCategory = (id: string, name: string) =>
  API.put(`/expense-categories/${id}`, { name }).then((r) => r.data);

export const deleteExpenseCategory = (id: string) =>
  API.delete(`/expense-categories/${id}`).then((r) => r.data);

// ===================== EXPENSES =====================
export type ExpenseRecord = {
  _id: string;
  expenseType: string;
  expenseCategory: string;
};

export const listExpenses = () =>
  API.get('/expenses').then((r) => unwrapList<ExpenseRecord>(r.data, ['expenses', 'items']));

export const createExpense = (payload: { expenseType: string; expenseCategory: string }) =>
  API.post('/expenses', payload).then((r) => r.data);

export const updateExpense = (id: string, payload: { expenseType: string; expenseCategory: string }) =>
  API.put(`/expenses/${id}`, payload).then((r) => r.data);

export const deleteExpense = (id: string) =>
  API.delete(`/expenses/${id}`).then((r) => r.data);

// ===================== PROFILE =====================
export const getProfile = () =>
  API.get('/profile/me').then(r => r.data);

export const updateProfile = (payload: { fullName: string; email: string; mobile: string }) =>
  API.put('/profile/me', payload).then(r => r.data);

export const changePassword = (payload: { currentPassword: string; newPassword: string }) =>
  API.put('/profile/me/password', payload).then(r => r.data);

// ===================== HOME ACTIVITY =====================
export const logPasswordGeneration = (payload: { password: string }) =>
  API.post('/home-activity/password-generations', payload).then((r) => r.data);

export const logInternetSpeed = (payload: {
  downloadMbps: number;
  uploadMbps: number;
  unloadedLatencyMs: number | null;
  downloadLoadedLatencyMs: number | null;
  uploadLoadedLatencyMs: number | null;
  connectionType?: string;
}) => API.post('/home-activity/internet-speed', payload).then((r) => r.data);

// ===================== TUNES =====================
export type UploadQuota = { count: number; limit: number | null; sizeLimit?: number | null };

export interface TuneItem {
  _id: string;
  title: string;
  artist?: string;
  originalName?: string;
  fileUrl: string;
  filename: string;
  size: number;
  mimetype: string;
  createdAt?: string;
  uploadedBy?: { _id?: string; username?: string; email?: string; fullName?: string; userType?: string };
}

export const listTunes = (search = '') =>
  API.get('/tunes', { params: { search } }).then((r) => ({
    items: unwrapList<TuneItem>(r.data, ['items', 'tunes']),
    quota: (r.data?.quota as UploadQuota | undefined) ?? null,
  }));

export const uploadTune = (payload: { title?: string; artist?: string; file: File }) => {
  const fd = new FormData();
  if (payload.title) fd.append('title', payload.title);
  if (payload.artist) fd.append('artist', payload.artist);
  fd.append('file', payload.file);
  return postOrPut('post', '/tunes', fd);
};

export const updateTune = (id: string, payload: { title: string; artist?: string }) =>
  API.put(`/tunes/${id}`, payload).then((r) => r.data);

export const deleteTune = (id: string) =>
  API.delete(`/tunes/${id}`).then((r) => r.data);

// ===================== VIDEOS =====================
export interface VideoItem {
  _id: string;
  slug?: string;
  title: string;
  videoType?: 'file' | 'link';
  originalName?: string;
  fileUrl?: string;
  filename?: string;
  linkUrl?: string;
  subtitleUrl?: string;
  size?: number;
  mimetype?: string;
  createdAt?: string;
  uploadedBy?: { _id?: string; username?: string; email?: string; fullName?: string; userType?: string };
  sharedWith?: VideoShareUser[];
  canAccess?: boolean;
}

export interface VideoShareUser {
  _id: string;
  username?: string;
  email?: string;
  fullName?: string;
}

export const listVideos = (search = '') =>
  API.get('/videos', { params: { search } }).then((r) => ({
    items: unwrapList<VideoItem>(r.data, ['items', 'videos']),
    quota: (r.data?.quota as UploadQuota | undefined) ?? null,
  }));

export const getVideo = (id: string) =>
  API.get(`/videos/${id}`).then((r) => r.data as VideoItem & { linkUrl?: string });

export const uploadVideo = (
  payload: { title: string; videoType?: string; file?: File; linkUrl?: string },
  onProgress?: (loaded: number, total: number) => void
) => {
  const fd = new FormData();
  fd.append('title', payload.title);
  fd.append('videoType', payload.videoType || 'file');
  if (payload.file) fd.append('file', payload.file);
  if (payload.linkUrl) fd.append('linkUrl', payload.linkUrl);
  return postOrPut('post', '/videos', fd, {
    timeout: ONE_HOUR_MS,
    ...(onProgress
      ? { onUploadProgress: (e: any) => onProgress(e.loaded || 0, e.total || payload.file?.size || 0) }
      : {}),
  });
};

export type VideoUploadJobStatus = {
  stage: 'queued' | 'checking' | 'converting' | 'saving' | 'done' | 'error';
  percent: number | null;
  video?: VideoItem;
  error?: string;
};

export const getVideoUploadJob = (jobId: string) =>
  API.get(`/videos/upload-jobs/${jobId}`).then((r) => r.data as VideoUploadJobStatus);

export const updateVideo = (id: string, payload: { title?: string; linkUrl?: string; file?: File }) => {
  if (payload.file) {
    const fd = new FormData();
    if (payload.title) fd.append('title', payload.title);
    if (payload.linkUrl) fd.append('linkUrl', payload.linkUrl);
    fd.append('file', payload.file);
    return postOrPut('put', `/videos/${id}`, fd, { timeout: ONE_HOUR_MS }) as Promise<VideoItem>;
  }
  return API.put(`/videos/${id}`, payload).then((r) => r.data as VideoItem);
};

export const unlockVideosMenu = (password: string) =>
  API.post('/videos/unlock', { password }).then((r) => r.data);

export const unlockVideoPlayback = (id: string, password?: string) =>
  API.post(`/videos/${id}/access`, password ? { password } : {}, { timeout: ONE_HOUR_MS }).then((r) => r.data as {
    success: boolean;
    videoType?: string;
    fileUrl?: string;
    linkUrl?: string;
    title?: string;
    subtitleUrl?: string;
  });

export const uploadVideoSubtitle = (id: string, file: File) => {
  const fd = new FormData();
  fd.append('subtitle', file);
  return postOrPut('post', `/videos/${id}/subtitle`, fd) as Promise<{ success: boolean; subtitleUrl?: string }>;
};

export const deleteVideoSubtitle = (id: string) =>
  API.delete(`/videos/${id}/subtitle`).then((r) => r.data as { success: boolean });

export const deleteVideo = (id: string) =>
  API.delete(`/videos/${id}`).then((r) => r.data);

export const getVideoSharedWith = (id: string) =>
  API.get(`/videos/${id}/shared-with`).then((r) => r.data as { sharedWith: VideoShareUser[] });

export const shareVideoWithFriends = (id: string, userIds: string[]) =>
  API.post(`/videos/${id}/share`, { userIds }).then((r) => r.data as {
    success: boolean;
    sharedWith: VideoShareUser[];
  });

// ===================== PLAYLISTS =====================
export interface PlaylistItem {
  _id: string;
  name: string;
  user?: string;
  tunes?: TuneItem[];
  createdAt?: string;
  updatedAt?: string;
}

export const listPlaylistsApi = () =>
  API.get('/playlists').then((r) => unwrapList<PlaylistItem>(r.data, ['items', 'playlists']));

export const createPlaylist = (name: string) =>
  API.post('/playlists', { name }).then((r) => r.data);

export const updatePlaylist = (id: string, name: string) =>
  API.put(`/playlists/${id}`, { name }).then((r) => r.data);

export const deletePlaylist = (id: string) =>
  API.delete(`/playlists/${id}`).then((r) => r.data);

export const getPlaylist = (id: string) =>
  API.get(`/playlists/${id}`).then((r) => r.data as PlaylistItem);

export const addTuneToPlaylist = (playlistId: string, tuneId: string) =>
  API.post(`/playlists/${playlistId}/tunes/${tuneId}`).then((r) => r.data as PlaylistItem);

// ===================== CHAT =====================
export const sendChatRequest = (email: string) =>
  API.post('/chat/requests', { email }).then((r) => r.data);

export const getChatRequests = () =>
  API.get('/chat/requests').then((r) => r.data);

export const acceptChatRequest = (id: string) =>
  API.post(`/chat/requests/${id}/accept`).then((r) => r.data);

export const declineChatRequest = (id: string) =>
  API.post(`/chat/requests/${id}/decline`).then((r) => r.data);

export const listChatThreads = () =>
  API.get('/chat/threads').then((r) => r.data);

export const getChatMessages = (threadId: string, limit = 50) =>
  API.get(`/chat/threads/${threadId}/messages`, { params: { limit } }).then((r) => r.data);

export const sendChatMessage = (threadId: string, text: string, files?: File[]) => {
  if (files && files.length > 0) {
    const fd = new FormData();
    if (text) fd.append('text', text);
    files.forEach((f) => fd.append('files', f));
    return API.post(`/chat/threads/${threadId}/messages`, fd).then((r) => r.data);
  }
  return API.post(`/chat/threads/${threadId}/messages`, { text }).then((r) => r.data);
};

// ===================== NOTE FACETS (categories/tags sidebar) =====================
export interface NoteFacet { name: string; count: number; }
export const getNoteFacets = () =>
  API.get('/notes/facets').then((r) => r.data as { total: number; categories: NoteFacet[]; tags: NoteFacet[] });

// ===================== PAYMENTS =====================
export type PaymentMethod = 'Cash' | 'Bank Transfer' | 'UPI' | 'Credit Card' | 'Debit Card' | 'Card' | 'Cheque' | 'Other';
export interface PaymentReceipt { _id: string; url: string; filename?: string; size?: number; mimetype?: string; }
export interface PaymentItem {
  _id: string;
  title: string;
  description?: string;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  person?: string;
  project?: { _id: string; name?: string } | null;
  category?: string | null;
  method?: PaymentMethod | null;
  tags?: string[];
  receipts?: PaymentReceipt[];
  reminderAt?: string | null;
  date: string;
  status: 'completed' | 'pending';
  createdAt?: string;
  updatedAt?: string;
}

export const listPayments = (params: { type?: string; status?: string; month?: string; search?: string } = {}) =>
  API.get('/payments', { params }).then((r) => unwrapList<PaymentItem>(r.data, ['items']));

export const getPaymentStats = (month?: string) =>
  API.get('/payments/stats', { params: { month } }).then((r) => r.data as {
    totalReceived: number; totalPaid: number; balance: number;
    receivedDeltaPct: number | null; paidDeltaPct: number | null;
  });

export const createPayment = (data: FormData) =>
  postOrPut('post', '/payments', data);

export const updatePayment = (id: string, data: FormData) =>
  postOrPut('put', `/payments/${id}`, data);

export const deletePayment = (id: string) =>
  API.delete(`/payments/${id}`).then((r) => r.data);

// ===================== GROCERY =====================
export interface GroceryItemRow {
  _id: string;
  name: string;
  category: string;
  quantity?: string;
  status: 'to_buy' | 'bought';
  favourite: boolean;
  addedOn: string;
  boughtOn?: string | null;
}

export type GroceryItemPayload = {
  name: string;
  category?: string;
  quantity?: string;
  favourite?: boolean;
  status?: 'to_buy' | 'bought';
};

export const listGroceryItems = (params: { status?: string; category?: string; favourite?: boolean; search?: string } = {}) =>
  API.get('/grocery-items', { params }).then((r) => unwrapList<GroceryItemRow>(r.data, ['items']));

export const getGroceryStats = () =>
  API.get('/grocery-items/stats').then((r) => r.data as { total: number; bought: number; toBuy: number; percentBought: number });

export const createGroceryItem = (data: GroceryItemPayload) =>
  postOrPut('post', '/grocery-items', data);

export const updateGroceryItem = (id: string, data: Partial<GroceryItemPayload>) =>
  postOrPut('put', `/grocery-items/${id}`, data);

export const toggleGroceryStatus = (id: string) =>
  API.patch(`/grocery-items/${id}/status`).then((r) => r.data as GroceryItemRow);

export const deleteGroceryItem = (id: string) =>
  API.delete(`/grocery-items/${id}`).then((r) => r.data);

// ===================== DOCUMENTS =====================
export interface DocumentFolderItem { _id: string; name: string; parent: string | null; }
export interface DocumentItem {
  _id: string;
  name: string;
  folder: string | null;
  url: string;
  filename?: string;
  size?: number;
  mimetype?: string;
  fileType: 'image' | 'pdf' | 'office' | 'other';
  createdAt?: string;
}

export const listDocumentFolders = (parent?: string) =>
  API.get('/documents/folders', { params: { parent } }).then((r) => unwrapList<DocumentFolderItem>(r.data, ['items']));

export const createDocumentFolder = (name: string, parent?: string | null) =>
  API.post('/documents/folders', { name, parent }).then((r) => r.data);

export const renameDocumentFolder = (id: string, name: string) =>
  API.put(`/documents/folders/${id}`, { name }).then((r) => r.data);

export const deleteDocumentFolder = (id: string) =>
  API.delete(`/documents/folders/${id}`).then((r) => r.data);

export const listDocuments = (params: { folder?: string; type?: string; search?: string } = {}) =>
  API.get('/documents', { params }).then((r) => unwrapList<DocumentItem>(r.data, ['items']));

export const getDocumentStats = () =>
  API.get('/documents/stats').then((r) => r.data as { total: number; images: number; pdf: number; office: number; others: number });

export const uploadDocument = (file: File, opts: { name?: string; folder?: string | null } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (opts.name) fd.append('name', opts.name);
  if (opts.folder) fd.append('folder', opts.folder);
  return postOrPut('post', '/documents', fd);
};

export const deleteDocument = (id: string) =>
  API.delete(`/documents/${id}`).then((r) => r.data);

// ===================== CALENDAR =====================
export interface CalendarFeedEvent {
  type: 'note' | 'payment' | 'custom';
  date: string;
  title: string;
  refId: string;
}

export const getCalendarFeed = (month?: string) =>
  API.get('/calendar-events', { params: { month } }).then((r) => (r.data?.events || []) as CalendarFeedEvent[]);

export const createCalendarEvent = (data: { title: string; date: string; notes?: string }) =>
  postOrPut('post', '/calendar-events', data);

export const updateCalendarEvent = (id: string, data: Partial<{ title: string; date: string; notes: string }>) =>
  postOrPut('put', `/calendar-events/${id}`, data);

export const deleteCalendarEvent = (id: string) =>
  API.delete(`/calendar-events/${id}`).then((r) => r.data);

// ===================== REPORTS =====================
export type ReportMetric = { count: number; deltaPct: number | null };

export const getReportsOverview = (from?: string, to?: string) =>
  API.get('/reports/overview', { params: { from, to } }).then((r) => r.data as {
    notes: ReportMetric; payments: ReportMetric; groceryItems: ReportMetric; documents: ReportMetric;
  });

export const getPaymentsTrend = (from?: string, to?: string) =>
  API.get('/reports/payments-trend', { params: { from, to } }).then((r) => r.data.buckets as {
    date: string; income: number; expense: number;
  }[]);

export const getTopPaymentCategories = (from?: string, to?: string) =>
  API.get('/reports/top-categories', { params: { from, to } }).then((r) => r.data.categories as {
    name: string; value: number; pct: number;
  }[]);
